import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Plus } from 'lucide-react';
import { listAllWatches, type WatchListItem } from '../services/techWatchService';
import { subProjectService, type SubProject } from '../services/subProjectService';
import { projectService, type Project } from '../services/projectService';
import { TechWatchConfigModal } from '../components/TechWatchConfigModal';
import PageHeader from '@/components/layout/PageHeader';
import SearchInput from '@/components/shared/SearchInput';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');

export default function Watches() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [watches, setWatches] = useState<WatchListItem[]>([]);
  const [subProjects, setSubProjects] = useState<SubProject[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('__all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused'>('all');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerProjectFilter, setPickerProjectFilter] = useState('__all');
  const [selectedSpId, setSelectedSpId] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configSpId, setConfigSpId] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true); setError(null);
      const [watchData, spData, projData] = await Promise.all([
        listAllWatches().then(r => r.data),
        subProjectService.getAll(),
        projectService.getProjects(),
      ]);
      setWatches(watchData); setSubProjects(spData); setProjects(projData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.watches.errorLoad'));
    } finally {
      setLoading(false);
    }
  }

  const watchedSpIds = useMemo(() => new Set(watches.map(w => w.sub_project_id)), [watches]);

  const uniqueProjects = useMemo(() => {
    const seen = new Map<string, string>();
    for (const w of watches) { if (w.project_id) seen.set(w.project_id, w.project_name); }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [watches]);

  const pickerSubProjects = useMemo(() =>
    subProjects
      .filter(sp => !watchedSpIds.has(sp.id))
      .filter(sp => pickerProjectFilter === '__all' || sp.project_id === pickerProjectFilter),
    [subProjects, watchedSpIds, pickerProjectFilter]
  );

  const filtered = useMemo(() =>
    watches
      .filter(w => {
        if (!search) return true;
        const q = search.toLowerCase();
        return w.sub_project_name.toLowerCase().includes(q) || w.project_name.toLowerCase().includes(q);
      })
      .filter(w => projectFilter === '__all' || w.project_id === projectFilter)
      .filter(w => statusFilter === 'all' || (statusFilter === 'active' ? w.is_active : !w.is_active)),
    [watches, search, projectFilter, statusFilter]
  );

  const StatusCell: React.FC<{ w: WatchListItem }> = ({ w }) => {
    if (w.is_active && w.latest_digest_status === 'pending_review')
      return <span className="inline-flex items-center gap-1 text-xs text-amber-600"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />{t('pages.watches.pending')}</span>;
    if (w.is_active)
      return <span className="inline-flex items-center gap-1 text-xs text-green-700"><span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />{t('techWatch.active')}</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-slate-500"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" />{t('techWatch.inactive')}</span>;
  };

  const SourcesBadges: React.FC<{ sources: WatchListItem['sources'] }> = ({ sources }) => {
    const counts: Record<string, number> = {};
    for (const s of sources) counts[s.type] = (counts[s.type] || 0) + 1;
    return (
      <>
        {Object.entries(counts).map(([type, n]) => (
          <Badge key={type} variant="secondary" className="text-xs py-0 px-1.5">
            {n}&nbsp;{type === 'rss' ? t('techWatch.sourceTypeBadgeRss') : type === 'arxiv' ? t('techWatch.sourceTypeBadgeArxiv') : type}
          </Badge>
        ))}
      </>
    );
  };

  const handleCreateClick = () => {
    setPickerProjectFilter(''); setSelectedSpId(''); setPickerOpen(true);
  };

  const handlePickerConfirm = () => {
    if (!selectedSpId) return;
    setPickerOpen(false);
    setConfigSpId(selectedSpId);
    setConfigModalOpen(true);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={t('navigation.watches')}
        actions={
          <Button size="sm" onClick={handleCreateClick}>
            <Plus size={14} className="mr-1" />{t('pages.watches.newWatch')}
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="flex items-center gap-2 mb-4">
          <SearchInput value={search} onChange={setSearch} placeholder={t('pages.watches.searchPlaceholder')} className="w-60" />
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="h-8 text-xs w-44"><SelectValue placeholder={t('pages.watches.allProjects')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t('pages.watches.allProjects')}</SelectItem>
              {uniqueProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pages.watches.statusAll')}</SelectItem>
              <SelectItem value="active">{t('techWatch.active')}</SelectItem>
              <SelectItem value="paused">{t('techWatch.inactive')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : error ? (
          <p className="text-sm text-destructive px-1">{error}</p>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Eye}
            title={t('pages.watches.empty')}
            description={t('pages.watches.emptyDescription')}
            action={{ label: t('pages.watches.newWatch'), onClick: handleCreateClick }}
          />
        ) : (
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 font-medium">{t('pages.watches.colSubProject')}</th>
                <th className="py-2 pr-4 font-medium">{t('pages.watches.colProject')}</th>
                <th className="py-2 pr-4 font-medium">{t('pages.watches.colSources')}</th>
                <th className="py-2 pr-4 font-medium">{t('pages.watches.colStatus')}</th>
                <th className="py-2 pr-4 font-medium">{t('pages.watches.colLastDigest')}</th>
                <th className="py-2 font-medium">{t('pages.watches.colDigestSnippet')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => (
                <tr
                  key={w.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/scv2/sub-project/${w.sub_project_id}`)}
                >
                  <td className="py-2.5 pr-4 font-medium text-primary-700">{w.sub_project_name || '—'}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{w.project_name || '—'}</td>
                  <td className="py-2.5 pr-4"><div className="flex flex-wrap gap-1"><SourcesBadges sources={w.sources} /></div></td>
                  <td className="py-2.5 pr-4"><StatusCell w={w} /></td>
                  <td className="py-2.5 pr-4 text-slate-500 text-xs whitespace-nowrap">{fmt(w.latest_digest_at)}</td>
                  <td className="py-2.5 max-w-xs text-slate-400 text-xs truncate">{w.digest_snippet || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Sub-project picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">{t('pages.watches.pickSubProject')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <Select value={pickerProjectFilter} onValueChange={v => { setPickerProjectFilter(v); setSelectedSpId(''); }}>
              <SelectTrigger className="h-8 text-xs w-full"><SelectValue placeholder={t('pages.watches.allProjects')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">{t('pages.watches.allProjects')}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedSpId} onValueChange={setSelectedSpId}>
              <SelectTrigger className="h-8 text-xs w-full"><SelectValue placeholder={t('pages.watches.selectSubProject')} /></SelectTrigger>
              <SelectContent>
                {pickerSubProjects.length === 0
                  ? <SelectItem value="__none" disabled>{t('pages.watches.noAvailableSubProjects')}</SelectItem>
                  : pickerSubProjects.map(sp => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)
                }
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setPickerOpen(false)}>{t('common.cancel')}</Button>
              <Button size="sm" disabled={!selectedSpId || selectedSpId === '__none'} onClick={handlePickerConfirm}>{t('common.next')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {configSpId && (
        <TechWatchConfigModal
          isOpen={configModalOpen}
          onClose={() => { setConfigModalOpen(false); setConfigSpId(''); }}
          subProjectId={configSpId}
          onSaved={() => { setConfigModalOpen(false); setConfigSpId(''); load(); }}
          onDeleted={() => { setConfigModalOpen(false); setConfigSpId(''); load(); }}
        />
      )}
    </div>
  );
}
